/**
 * PhysicalDetailsStep — Step 1 of the physical checkout variant.
 *
 * Renders:
 *   - Product preview (title + seller name).
 *   - Variation selector (if the product has variations — pre-selects the
 *     first in-stock option, with stock + price-delta labels).
 *   - Quantity selector (1-10 per MAX_QUANTITY, Minus/Input/Plus layout).
 *   - Fulfillment method selection (ship vs pickup RadioGroup with the
 *     EFFECTIVE flags — `effectiveShips` / `effectivePickup`).
 *   - Conditional shipping address fieldset (when `fulfillmentMethod ===
 *     'ship'`): saved address book selector + name/phone/zip/address/
 *     city/country inputs + indicaciones textarea + "Guardar esta
 *     dirección" checkbox + label input (Sprint 139 PR-H).
 *   - Conditional pickup confirmation block (when `fulfillmentMethod ===
 *     'pickup'`): location, hours, instructions, Google Maps link + the
 *     "Confirmo que pasaré a recoger" checkbox.
 *   - Inline error alert + footer (Cancelar / Continuar → Step 2).
 *
 * SPEC-244 · partición VERBATIM de
 * frontend/src/components/market/checkout-modal.tsx (2.637 L).
 *
 * Preserved VERBATIM — the original Step 1 JSX block (lines 944-1480 of
 * the monolith). Only `props` are threaded from the parent hook
 * (T-PROPS-PASSING).
 */

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { DialogFooter } from '@/components/ui/dialog';
import type * as React from 'react';
import {
  Truck,
  MapPin,
  ArrowRight,
  Minus,
  Plus,
  ExternalLink,
} from 'lucide-react';
import type {
  PublicProductResponse,
  FulfillmentMethod,
  ShippingAddress,
  VariationSelection,
} from '@profileia/shared';
import { MAX_QUANTITY } from '../constants';
import { formatCredits, resolveShipping } from '../helpers';
import { SubmitErrorAlert } from './submit-error-alert';
import { SavedAddressSelector } from './saved-address-selector';

// ─── Component ───────────────────────────────────────────────────────────────

export interface PhysicalDetailsStepProps {
  product: PublicProductResponse;
  effectiveShips: boolean;
  effectivePickup: boolean;
  quantity: number;
  setQuantity: React.Dispatch<React.SetStateAction<number>>;
  fulfillmentMethod: FulfillmentMethod | null;
  setFulfillmentMethod: React.Dispatch<React.SetStateAction<FulfillmentMethod | null>>;
  variation: PublicProductResponse['variations'][number] | undefined;
  hasVariations: boolean;
  variationSelection: VariationSelection | null;
  setVariationSelection: React.Dispatch<React.SetStateAction<VariationSelection | null>>;
  shippingAddress: ShippingAddress;
  setShippingAddress: React.Dispatch<React.SetStateAction<ShippingAddress>>;
  savedAddresses: import('@profileia/shared').SavedAddressResponse[];
  savedAddressesLoading: boolean;
  selectedSavedAddressId: string;
  handleSelectSavedAddress: (savedAddressId: string) => void;
  indicaciones: string;
  setIndicaciones: React.Dispatch<React.SetStateAction<string>>;
  saveThisAddress: boolean;
  setSaveThisAddress: React.Dispatch<React.SetStateAction<boolean>>;
  addressLabel: string;
  setAddressLabel: React.Dispatch<React.SetStateAction<string>>;
  pickupConfirmed: boolean;
  setPickupConfirmed: React.Dispatch<React.SetStateAction<boolean>>;
  pickupInfo: {
    address: string;
    lat: number;
    lng: number;
    label: string | null;
    instructions: string;
    hours: string;
  } | null;
  pickupLoc: { lat: number; lng: number; address: string } | null;
  mapsLink: string | null;
  detailsValid: boolean;
  submitError: string | null;
  onClose: () => void;
  onContinue: () => void;
}

export function PhysicalDetailsStep({
  product,
  effectiveShips,
  effectivePickup,
  quantity,
  setQuantity,
  fulfillmentMethod,
  setFulfillmentMethod,
  variation,
  hasVariations,
  variationSelection,
  setVariationSelection,
  shippingAddress,
  setShippingAddress,
  savedAddresses,
  savedAddressesLoading,
  selectedSavedAddressId,
  handleSelectSavedAddress,
  indicaciones,
  setIndicaciones,
  saveThisAddress,
  setSaveThisAddress,
  addressLabel,
  setAddressLabel,
  pickupConfirmed,
  setPickupConfirmed,
  pickupInfo,
  pickupLoc,
  mapsLink,
  detailsValid,
  submitError,
  onClose,
  onContinue,
}: PhysicalDetailsStepProps) {
  return (
    <div className="space-y-4">
      {/* Product preview (title + price) — keeps the user oriented. */}
      <div className="rounded-md border border-border bg-muted/40 p-3">
        <p className="text-sm font-medium line-clamp-2">{product.title}</p>
        <p className="text-xs text-muted-foreground mt-1">
          Vendido por <span className="font-medium">{product.seller.storeName}</span>
        </p>
      </div>

      {/* Variation selector (if applicable) */}
      {hasVariations && variation && (
        <div>
          <Label
            htmlFor="checkout-variation-select"
            className="mb-1.5 block text-sm font-medium"
          >
            {variation.name}
          </Label>
          <select
            id="checkout-variation-select"
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={`Seleccionar ${variation.name}`}
            value={variationSelection?.option ?? ''}
            onChange={(e) => {
              const option = e.target.value;
              if (!option) {
                setVariationSelection(null);
                return;
              }
              setVariationSelection({ name: variation.name, option });
            }}
          >
            {variation.options.map((opt) => {
              const delta = parseFloat(opt.priceDelta);
              const base = parseFloat(product.priceCredits);
              const total = base + delta;
              const priceLabel =
                delta === 0
                  ? `${formatCredits(total)} créditos`
                  : delta > 0
                    ? `${formatCredits(total)} créditos (+${formatCredits(delta)})`
                    : `${formatCredits(total)} créditos (${formatCredits(delta)})`;
              return (
                <option
                  key={opt.name}
                  value={opt.name}
                  disabled={opt.stock === 0}
                >
                  {opt.name} — {priceLabel} —{' '}
                  {opt.stock > 0 ? `${opt.stock} disponibles` : 'Agotado'}
                </option>
              );
            })}
          </select>
        </div>
      )}

      {/* Quantity selector (1-10) */}
      <div>
        <Label
          htmlFor="checkout-quantity"
          className="mb-1.5 block text-sm font-medium"
        >
          Cantidad
        </Label>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="size-9"
            onClick={() => setQuantity((q) => Math.max(1, q - 1))}
            disabled={quantity <= 1}
            aria-label="Disminuir cantidad"
          >
            <Minus className="size-4" aria-hidden="true" />
          </Button>
          <Input
            id="checkout-quantity"
            type="number"
            min={1}
            max={MAX_QUANTITY}
            value={quantity}
            onChange={(e) => {
              const raw = parseInt(e.target.value, 10);
              if (Number.isNaN(raw)) {
                setQuantity(1);
                return;
              }
              setQuantity(Math.min(MAX_QUANTITY, Math.max(1, raw)));
            }}
            className="w-20 text-center"
            aria-label={`Cantidad (máximo ${MAX_QUANTITY})`}
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="size-9"
            onClick={() =>
              setQuantity((q) => Math.min(MAX_QUANTITY, q + 1))
            }
            disabled={quantity >= MAX_QUANTITY}
            aria-label="Aumentar cantidad"
          >
            <Plus className="size-4" aria-hidden="true" />
          </Button>
          <span className="text-xs text-muted-foreground ml-1">
            Máximo {MAX_QUANTITY} unidades por orden.
          </span>
        </div>
      </div>

      {/* Fulfillment method selection (ship vs pickup) */}
      <div>
        <p className="mb-2 text-sm font-medium" id="fulfillment-label">
          Método de entrega
        </p>
        {!effectiveShips && !effectivePickup && (
          <p className="text-sm text-destructive">
            Este vendedor no ofrece opciones de entrega. Contacta al vendedor para coordinar.
          </p>
        )}
        <RadioGroup
          aria-labelledby="fulfillment-label"
          value={fulfillmentMethod ?? ''}
          onValueChange={(v) => setFulfillmentMethod(v as FulfillmentMethod)}
          className="space-y-2"
        >
          {effectiveShips && (
            <Label
              htmlFor="fulfillment-ship"
              className="flex items-start gap-3 rounded-md border border-border p-3 cursor-pointer hover:bg-accent transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
            >
              <RadioGroupItem
                id="fulfillment-ship"
                value="ship"
                className="mt-1"
              />
              <span className="flex-1">
                <span className="flex items-center gap-1.5 text-sm font-medium">
                  <Truck className="size-4" aria-hidden="true" />
                  Envío a domicilio
                </span>
                <span className="block text-xs text-muted-foreground mt-0.5">
                  El vendedor envía la orden a la dirección que indiques.
                  {' '}
                  {resolveShipping(product) && (
                    <>
                      Costo base:{' '}
                      <span className="font-medium">
                        {resolveShipping(product)!.cost} USDT
                      </span>
                      {resolveShipping(product)!.freeShippingThreshold && (
                        <>
                          {' '}— Envío gratis desde{' '}
                          <span className="font-medium">
                            {resolveShipping(product)!.freeShippingThreshold} USDT
                          </span>
                        </>
                      )}
                      .
                    </>
                  )}
                </span>
              </span>
            </Label>
          )}
          {effectivePickup && (
            <Label
              htmlFor="fulfillment-pickup"
              className="flex items-start gap-3 rounded-md border border-border p-3 cursor-pointer hover:bg-accent transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
            >
              <RadioGroupItem
                id="fulfillment-pickup"
                value="pickup"
                className="mt-1"
              />
              <span className="flex-1">
                <span className="flex items-center gap-1.5 text-sm font-medium">
                  <MapPin className="size-4" aria-hidden="true" />
                  Recogida en local
                </span>
                <span className="block text-xs text-muted-foreground mt-0.5">
                  Recoges la orden en el local del vendedor. Sin costo de envío.
                </span>
              </span>
            </Label>
          )}
        </RadioGroup>
      </div>

      {/* Conditional: shipping address form (if ship selected) */}
      {fulfillmentMethod === 'ship' && (
        <fieldset
          className="space-y-3 rounded-md border border-border p-3"
          aria-label="Dirección de envío"
        >
          <legend className="px-1 text-sm font-medium">
            Dirección de envío
          </legend>

          {/* Sprint 139 (PR-H: Saved Shipping Addresses):
              address book selector. Shown only if the user has
              saved addresses. If 0 saved addresses OR fetch failed,
              the user just types a new address (no selector visible
              — same UX as pre-PR-H). */}
          {savedAddresses.length > 0 && (
            <SavedAddressSelector
              savedAddresses={savedAddresses}
              savedAddressesLoading={savedAddressesLoading}
              selectedSavedAddressId={selectedSavedAddressId}
              onSelect={handleSelectSavedAddress}
            />
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label htmlFor="ship-name" className="text-xs">
                Nombre del destinatario
              </Label>
              <Input
                id="ship-name"
                value={shippingAddress.name}
                onChange={(e) =>
                  setShippingAddress((s) => ({ ...s, name: e.target.value }))
                }
                autoComplete="name"
                maxLength={120}
                required
              />
            </div>
            <div>
              <Label htmlFor="ship-phone" className="text-xs">
                Teléfono
              </Label>
              <Input
                id="ship-phone"
                value={shippingAddress.phone}
                onChange={(e) =>
                  setShippingAddress((s) => ({ ...s, phone: e.target.value }))
                }
                autoComplete="tel"
                maxLength={30}
                required
              />
            </div>
            <div>
              <Label htmlFor="ship-zip" className="text-xs">
                Código postal
              </Label>
              <Input
                id="ship-zip"
                value={shippingAddress.zip}
                onChange={(e) =>
                  setShippingAddress((s) => ({ ...s, zip: e.target.value }))
                }
                autoComplete="postal-code"
                maxLength={20}
                required
              />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="ship-address" className="text-xs">
                Dirección
              </Label>
              <Input
                id="ship-address"
                value={shippingAddress.address}
                onChange={(e) =>
                  setShippingAddress((s) => ({ ...s, address: e.target.value }))
                }
                autoComplete="street-address"
                maxLength={300}
                required
              />
            </div>
            <div>
              <Label htmlFor="ship-city" className="text-xs">
                Ciudad
              </Label>
              <Input
                id="ship-city"
                value={shippingAddress.city}
                onChange={(e) =>
                  setShippingAddress((s) => ({ ...s, city: e.target.value }))
                }
                autoComplete="address-level2"
                maxLength={120}
                required
              />
            </div>
            <div>
              <Label htmlFor="ship-country" className="text-xs">
                País
              </Label>
              <Input
                id="ship-country"
                value={shippingAddress.country}
                onChange={(e) =>
                  setShippingAddress((s) => ({ ...s, country: e.target.value }))
                }
                autoComplete="country-name"
                maxLength={80}
                required
              />
            </div>

            {/* Sprint 139 (PR-H): Indicaciones para la entrega.
                Free-text delivery instructions from the buyer
                (ej: "dejar en portería", "timbre 3B", "llamar
                antes de entregar"). Optional — buyer can leave empty.
                Persisted in the order snapshot (immutable per order)
                AND optionally in the SavedAddress book. */}
            <div className="sm:col-span-2">
              <Label htmlFor="ship-indicaciones" className="text-xs">
                Indicaciones para la entrega <span className="text-muted-foreground">(opcional)</span>
              </Label>
              <Textarea
                id="ship-indicaciones"
                value={indicaciones}
                onChange={(e) => {
                  setIndicaciones(e.target.value.slice(0, 500));
                  // Sync the indicaciones into the shippingAddress
                  // state so the order snapshot includes it when
                  // the buyer submits.
                  setShippingAddress((s) => ({
                    ...s,
                    indicaciones: e.target.value.slice(0, 500),
                  }));
                }}
                placeholder="Ej: Dejar en portería · Timbre 3B · Llamar 5 min antes de llegar"
                rows={2}
                maxLength={500}
                aria-describedby="ship-indicaciones-help"
              />
              <p id="ship-indicaciones-help" className="text-xs text-muted-foreground">
                Máximo 500 caracteres. Visible para el vendedor al preparar el envío.
              </p>
            </div>

            {/* Sprint 139 (PR-H): "Guardar esta dirección" checkbox.
                Shown only when the user is typing a new address
                (selectedSavedAddressId === 'new'). When checked +
                the order is created, the frontend POSTs the new
                address to /api/me/addresses (so the next checkout
                pre-fills it). */}
            {selectedSavedAddressId === 'new' && (
              <div className="sm:col-span-2 space-y-2 rounded-md border border-dashed border-border/60 bg-muted/20 p-3">
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="save-address"
                    checked={saveThisAddress}
                    onCheckedChange={(checked) =>
                      setSaveThisAddress(checked === true)
                    }
                    className="mt-0.5"
                  />
                  <div className="min-w-0 flex-1">
                    <Label htmlFor="save-address" className="text-sm font-medium cursor-pointer">
                      Guardar esta dirección para próximas compras
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      La guardamos en tu address book para pre-llenar automáticamente (como en MercadoLibre). Máximo 10 direcciones.
                    </p>
                  </div>
                </div>
                {saveThisAddress && (
                  <div>
                    <Label htmlFor="address-label" className="text-xs">
                      Nombre para esta dirección <span className="text-muted-foreground">(ej: Casa, Trabajo)</span>
                    </Label>
                    <Input
                      id="address-label"
                      value={addressLabel}
                      onChange={(e) => setAddressLabel(e.target.value.slice(0, 50))}
                      placeholder="Casa"
                      maxLength={50}
                      required={saveThisAddress}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </fieldset>
      )}

      {/* Conditional: pickup confirmation (if pickup selected) */}
      {fulfillmentMethod === 'pickup' && pickupInfo && pickupLoc && (
        <div className="space-y-3 rounded-md border border-border p-3">
          <div>
            <p className="text-sm font-medium flex items-center gap-1.5">
              <MapPin className="size-4" aria-hidden="true" />
              Ubicación de recogida
            </p>
            {pickupInfo.label && (
              <p className="mt-0.5 text-xs text-muted-foreground">{pickupInfo.label}</p>
            )}
            <address className="mt-1 text-sm not-italic text-muted-foreground">
              {pickupLoc.address}
            </address>
            {pickupInfo.hours && (
              <p className="mt-1 text-xs text-muted-foreground">
                Horarios: <span className="font-medium">{pickupInfo.hours}</span>
              </p>
            )}
            {pickupInfo.instructions && (
              <p className="mt-1 text-xs text-muted-foreground">
                Instrucciones: {pickupInfo.instructions}
              </p>
            )}
            {mapsLink && (
              <a
                href={mapsLink}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-xs text-primary hover:underline"
              >
                <ExternalLink className="size-3" aria-hidden="true" />
                Ver en Google Maps
              </a>
            )}
          </div>
          <Label
            htmlFor="pickup-confirm"
            className="flex items-start gap-2 text-sm cursor-pointer"
          >
            <Checkbox
              id="pickup-confirm"
              checked={pickupConfirmed}
              onCheckedChange={(v) => setPickupConfirmed(v === true)}
              className="mt-0.5"
            />
            <span>
              Confirmo que pasaré a recoger la orden en la ubicación
              indicada durante los horarios del vendedor.
            </span>
          </Label>
        </div>
      )}

      {submitError && <SubmitErrorAlert submitError={submitError} />}

      <DialogFooter className="sticky bottom-0 -mx-6 -mb-6 mt-2 flex-row gap-2 border-t border-border bg-background px-6 py-3 sm:flex-row">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          className="flex-1"
        >
          Cancelar
        </Button>
        <Button
          type="button"
          className="flex-1"
          disabled={!detailsValid}
          onClick={onContinue}
        >
          Continuar
          <ArrowRight className="size-4 ml-1" aria-hidden="true" />
        </Button>
      </DialogFooter>
    </div>
  );
}
